"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionModelState = void 0;
const tslib_1 = require("tslib");
const mobx_1 = require("mobx");
const geometry_factory_pitch_1 = require("../../math/geometry/geometry.factory.pitch");
const fetchable_1 = require("../../rest/fetchable");
const ike_map_1 = require("../ike.map");
class CollectionModelState {
    /**
     * @throws IkeFetchableRaceError if the capture fails to load due to a race condition.
     */
    static createGeometry3D(state, captureId) {
        const capture = state.getCapture(captureId);
        if (capture == null) {
            throw new fetchable_1.IkeFetchableRaceError(`Unable to find capture ${captureId}`);
        }
        const calibration = state.getCalibration(capture.calibrationid);
        if (calibration == null) {
            throw new Error(`Capture ${captureId}: Unable to find calibration ${capture.calibrationid}`);
        }
        return geometry_factory_pitch_1.PitchGeometryFactory.create({
            pitch: capture.orientation.pitch,
            distance: capture.distance,
            resolution: capture.resolution,
            calibration,
        });
    }
    constructor(loader) {
        /** Are changes allowed to be made */
        this.frozen = false;
        this.collectedAt = null;
        this.fields = new ike_map_1.IkeMap();
        this.geometries = new ike_map_1.IkeMap();
        this.drawableStates = mobx_1.observable.map();
        this.loader = loader;
    }
    /** Whether the state has uncommitted changes. Overridden by proxy state. */
    get isDirty() {
        return false;
    }
    getFieldValue(fieldId) {
        return this.fields.get(fieldId);
    }
    setFieldValue(fieldId, state) {
        if (this.frozen) {
            throw new Error(`Field ${fieldId}: Writing frozen state.`);
        }
        this.fields.set(fieldId, state);
    }
    getDrawable(captureId, drawableId) {
        const drawableState = this.drawableStates.get(captureId);
        if (drawableState === null || drawableState === void 0 ? void 0 : drawableState.hasDrawable(drawableId)) {
            return drawableState.getDrawable(drawableId);
        }
        throw new Error(`Unable to find drawable (id=${drawableId}) for capture ${captureId}`);
    }
    /**
     * Does a value for this field id exist
     *
     * @param fieldId id to check
     * @returns True if some data exists, false otherwise
     */
    hasFieldValue(fieldId) {
        return this.getFieldValue(fieldId) != null;
    }
    /**
     * Get a related capture object from its id
     *
     * Since not all of our data structures have access to the objectId
     * We need to guess if this is a uuid or a objectId
     *
     * @param captureId Capture.objectId or Capture.uuid
     *
     * @deprecated Use {@link IkeCollectionLoader} directly with async methods instead.
     */
    getCapture(captureId) {
        return this.loader.Capture.getById(captureId).value;
    }
    /**
     * Get a calibration given its objectId
     * This is lazy loaded and will return null until the value is loaded
     * @param calibrationId Id of the calibration to fetch
     */
    getCalibration(calibrationId) {
        return this.loader.Calibration.getById(calibrationId).value;
    }
    /**
     * @throws IkeFetchableRaceError if the capture fails to load due to a race condition.
     */
    getGeometry(captureId) {
        let existing = this.geometries.get(captureId);
        if (existing == null) {
            existing = CollectionModelState.createGeometry3D(this, captureId);
            this.geometries.set(captureId, existing);
        }
        return existing;
    }
    /**
     * Get a collection given its uuid
     *
     * This is lazy loaded and will return null until the value is loaded
     *
     * @param collectionId objectId of the collection to fetch
     */
    getCollection(collectionId) {
        return this.loader.CollectionSummary.getById(collectionId).value;
    }
    /**
     * @deprecated This method has a race condition when retrieving linked collections. Use getCollectionModelAsync instead.
     */
    getCollectionModel(collectionId) {
        var _a, _b;
        const collectionSummary = (_a = this.loader.CollectionSummary.getById(collectionId)) === null || _a === void 0 ? void 0 : _a.value;
        if (collectionSummary == null) {
            return null;
        }
        return (_b = this.loader.Collection.getModel(collectionSummary.revision)) === null || _b === void 0 ? void 0 : _b.value;
    }
    getCollectionModelAsync(collectionId) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const collectionSummary = yield this.loader.CollectionSummary.getById(collectionId).fetch();
            if (collectionSummary == null) {
                return null;
            }
            return yield this.loader.Collection.getModel(collectionSummary.revision).fetch();
        });
    }
    /**
     * Freeze the object no changes to field data will be allowed after the state is frozen.
     */
    freeze() {
        this.frozen = true;
    }
}
exports.CollectionModelState = CollectionModelState;
//# sourceMappingURL=collection.state.js.map